import Vue from 'vue';
import { startOfISOWeek } from 'date-fns';

import { SLOT_INTERVAL_MS, COVERED_BY_SLOT_COUNT } from "../../../constants_calendar";
import { initialSlotData } from "../utilities/calendarUtilities";

const ONE_WEEK_MS = 604800000;

const getDefaultState = () => {
  return {
    /*
      Contains all slot data, 
      key: dateTimeMS -- slot datetime as ms from unix epoch
      value: see  -- slot info (matchPoolUsersForSlot, selectability, availability etc)
    */
    calendarData: {},
    
    week: true,
  
    dataStartMS: Math.floor(Date.now()/SLOT_INTERVAL_MS)*SLOT_INTERVAL_MS,
    dataEndMS: Math.floor(Date.now()/SLOT_INTERVAL_MS)*SLOT_INTERVAL_MS,
    viewStartMS: startOfISOWeek(Date.now()).valueOf(),
    viewEndMS: startOfISOWeek(Date.now()).valueOf() + ONE_WEEK_MS,
  
    /* Contains user's matching preferences and matching data */
    /**
     * currentUserData: {
     *  attendanceScore: {Number}
     *  defaultRematch: {Boolean}
     *  metadata: {Object}
     *  preferences: {Object}         // public preferences
     *  privatePreferences: {Object}  // private preferences
     * }
     */
    currentUserData: null, 

    blockedExcludedIDs: [],
    
  };
};


const state = getDefaultState(); 

const getters = {
  getDataStart: (state) => state.dataStartMS,
  getDataEnd: (state) => state.dataEndMS,
  getDefaultRematch: (state) => state.currentUserData ? state.currentUserData.defaultRematch : true,
  getCurrentUserMetadata: (state) => state.currentUserData ? state.currentUserData.metadata : null,
  getCurrentUserPublicPreferences: (state) => state.currentUserData ? state.currentUserData.preferences : null,
};

const mutations = {
  SET_DATA_START(state, newStart) {
    state.dataStartMS = newStart;
  },
  SET_DATA_END(state, newEnd) {
    state.dataEndMS = newEnd;
  },
  SET_VIEW_START(state, newStart) {
    state.viewStartMS = newStart;
  },
  SET_VIEW_END(state, newEnd) {
    state.viewEndMS = newEnd;
  },
  SET_WEEK(state, newWeek) {
    state.week = newWeek;
  },
  /**
   * Adds keys to the calendarData
   * @param {
   *  rangeStart: {Number} the start date (inclusive)
   *  rangeEnd: {Number} the end date (inclusive )
   * } payload
   */
  ADD_DATE_TIMES(state, payload) {
    const {rangeStart, rangeEnd} = payload;
    for (let dateTime=rangeStart; dateTime <=rangeEnd; dateTime+=SLOT_INTERVAL_MS) {
      if (!state.calendarData[dateTime]) {
        Vue.set(state.calendarData, dateTime, initialSlotData());
      }
    }
  },

  /* ======== CALENDARDATA FIELD SETTERS ======== */

  SET_ONE_SLOT_FIELD_STATUS(state, payload) {
    const {dateTimeMS, field, value} = payload;
    state.calendarData[dateTimeMS][field] = value;
  },
  SET_SLOT_ARRAY_FIELD_STATUS(state, payload) {
    /* sets all in dateTimeMSArray to the *one* selectionValue value provided */
    const {dateTimeMSArray, field, value} = payload;
    for (let i=0; i<dateTimeMSArray.length; i++) {
      state.calendarData[dateTimeMSArray[i]][field] = value;
    }
  },
  
  /* ======== CURRENT USER MATCHING AND PREFERENCE DATA ======== */
  /**
   * Set the whole user data
   */
  SET_CURRENT_USER_DATA(state, user) {
    state.currentUserData = user;
  },
  /**
   * Set public preferences
   */
  SET_PUBLIC_PREFERENCES(state, newPreferences) {
    Vue.set(state.currentUserData, "preferences", newPreferences);
  },
  /**
   * Set private preferences
   */
  SET_PRIVATE_PREFERENCES(state, newPreferences) {
    Vue.set(state.currentUserData, "privatePreferences", newPreferences);
  },
  SET_DEFAULT_REMATCH(state, newValue) {
    Vue.set(state.currentUserData, "defaultRematch", newValue);
  },

  SET_BLOCKED_EXCLUDED_IDS(state, list) {
    state.blockedExcludedIDs = list;
  }
};

const actions = {
  /** 
   * Set's the new view for the calendar. 
   * @param {
    *  viewStart: {Number} the new view start 
    *  viewEnd: {Number} the new view end
    * }
    */
   setView({commit}, payload) {
     /* console.log("@setView payload: ", payload); */
     commit("SET_VIEW_START", payload.viewStart);
     commit("SET_VIEW_END", payload.viewEnd);
   },
   setWeek({commit}, newWeek) {
     commit("SET_WEEK", newWeek);
   },
  /**
   * Add new calendar datetime range and updates allowed data ranges correspondingly
   * @param {
   *  rangeStart: {Number} the start of the range to add (inclusive)
   *  rangeEnd: {Number} the end of the range to add (inclusive)
   * }
   */
  addDateTimeRange({state, commit}, payload) {
    let {rangeStart, rangeEnd} = payload;
    /* make sure datetimes are valid */
    rangeStart = Math.floor(rangeStart/SLOT_INTERVAL_MS)*SLOT_INTERVAL_MS;
    rangeEnd = Math.floor(rangeEnd/SLOT_INTERVAL_MS+1)*SLOT_INTERVAL_MS;
    if (state.dataStartMS > rangeStart) {
      /* add earlier datetimes */
      commit("ADD_DATE_TIMES", { rangeStart, rangeEnd: state.dataStartMS });
      commit("SET_DATA_START", rangeStart);
    }
    if (state.dataEndMS < rangeEnd) {
      /* add later datetimes */
      commit("ADD_DATE_TIMES", { rangeStart: state.dataEndMS, rangeEnd });
      commit("SET_DATA_END", rangeEnd);
    }
  },
  /**
   * Set at one datetime one field to new value
   * @param {
   *  dateTimeMS {Number/String},
   *  field,
   *  value
   * } payload
   */
  setOneSlotFieldStatus({commit}, payload) {
    commit("SET_ONE_SLOT_FIELD_STATUS", payload);
  },
  /**
   * Set at all datetimes one field to *the same* new value
   * @param {
   *  dateTimeMSArray {Array},
   *  field {String}, -- field to change
   *  value -- new value
   * } payload
   */
  setSlotArrayFieldStatus({commit}, payload) {
    commit("SET_SLOT_ARRAY_FIELD_STATUS", payload);
  },

  /**
   * Handles availability change after an upcoming match was deleted. 
   * @param {
   *  dateTimesMS: {Array} *sorted* array of datetimes 
   *  deletedMS: {Number} the date for which the upcoming match got deleted 
   * }
   */
  updateAvailabilitiesAfterDeletion({dispatch}, payload) {
    const {dateTimesMS, deletedMS} = payload;
    console.log("@updateAvailabilitiesAfterDeletion: ", dateTimesMS, deletedMS);
    let previous = 0, next = 0;
    for (let i=0; i<dateTimesMS.length; i++) {
      if (deletedMS > dateTimesMS[i]) previous = dateTimesMS[i];
      else {
        next = dateTimesMS[i];
        break; 
      }
    }
    console.log("previous: ", previous, "next:", next);
    const dateTimeMSArray = [];
    /* calculate slots that need to be updated */
    for (let i=-COVERED_BY_SLOT_COUNT; i<=COVERED_BY_SLOT_COUNT; i++) {
      const value = deletedMS + i*SLOT_INTERVAL_MS;
      if (previous && value <= previous + COVERED_BY_SLOT_COUNT*SLOT_INTERVAL_MS) continue;
      if (next && value >= next - COVERED_BY_SLOT_COUNT*SLOT_INTERVAL_MS) continue;
      dateTimeMSArray.push(value);
    }
    console.log("@updateAv.Af.Deletion, array: ", dateTimeMSArray);
    dispatch("setSlotArrayFieldStatus", {
      dateTimeMSArray,
      field: "isAvailableForBooking",
      value: true,
    });
  },

  /* ================ USER DATA ================ */
  setCurrentUserData({commit}, userData) {
    commit("SET_CURRENT_USER_DATA", userData);
  },

  setPublicPreferences({state, commit}, newPreferences) {
    if (state.currentUserData) {
      console.log("@dispatch successful, ", newPreferences);
      commit("SET_PUBLIC_PREFERENCES", newPreferences);
    }
  },
  setPrivatePreferences({state, commit}, newPreferences) {
    if (state.currentUserData) {
      commit("SET_PRIVATE_PREFERENCES", newPreferences);
    }
  },
  setDefaultRematch({state, commit}, newValue) {
    if (state.currentUserData && state.currentUserData.defaultRematch !== newValue) {
      commit("SET_DEFAULT_REMATCH", newValue);
    }
  },

  setBlockedExcludedIDs({commit}, list) {
    commit("SET_BLOCKED_EXCLUDED_IDS", list);
  }
}

const calendar = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default calendar;